import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';

import { registerOrgIdStore } from './orgIdAccessor';
import type { Run } from '@gw/ui/types';
import { createAnalyticsSlice, type AnalyticsSlice } from './slices/analyticsSlice';
import { createApplicationsSlice, type ApplicationsSlice } from './slices/applicationsSlice';
import { createAuthSlice, type AuthSlice } from './slices/authSlice';
import { createBuilderSlice, type BuilderSlice } from './slices/builderSlice';
import { createConfigSlice, type ConfigSlice } from './slices/configSlice';
import {
  createConsultantProfileSlice,
  type ConsultantProfileSlice,
} from './slices/consultantProfileSlice';
import { createDatasetQueriesSlice, type DatasetQueriesSlice } from './slices/datasetQueriesSlice';
import { createDatasetsSlice, type DatasetsSlice } from './slices/datasetsSlice';
import { createDatasetWizardSlice, type DatasetWizardSlice } from './slices/datasetWizardSlice';
import { createFeatureFlagsSlice, type FeatureFlagsSlice } from './slices/featureFlagsSlice';
import { createGiftCardsSlice, type GiftCardsSlice } from './slices/giftCardsSlice';
import { createItemsSlice, type ItemsSlice } from './slices/itemsSlice';
import { createMarkupSlice, type MarkupSlice } from './slices/markupSlice';
import {
  createMissionBriefingSlice,
  type MissionBriefingSlice,
} from './slices/missionBriefingSlice';
import { createMissionConfigSlice, type MissionConfigSlice } from './slices/missionConfigSlice';
import { createMissionCreateSlice, type MissionCreateSlice } from './slices/missionCreateSlice';
import {
  createMissionCreateSearchContextSlice,
  type MissionCreateSearchContextSlice,
} from './slices/missionCreateSearchContextSlice';
import {
  createMissionPlanningSlice,
  type MissionPlanningSlice,
} from './slices/missionPlanningSlice';
import {
  createMissionRunResultsSlice,
  type MissionRunResultsSlice,
} from './slices/missionRunResultsSlice';
import { createMissionRunsSlice, type MissionRunsSlice } from './slices/missionRunsSlice';
import { createMissionsSlice, type MissionScope, type MissionsSlice } from './slices/missionsSlice';
import { createOrganizationSlice, type OrganizationSlice } from './slices/organizationSlice';
import {
  createOrganizationTokensSlice,
  type OrganizationTokensSlice,
} from './slices/organizationTokensSlice';
import {
  createOrganizationTransactionsSlice,
  type OrganizationTransactionsSlice,
} from './slices/organizationTransactionsSlice';
import {
  createOrgInvoiceRequestsSlice,
  type OrgInvoiceRequestsSlice,
} from './slices/orgInvoiceRequestsSlice';
import { createOSINTSlice, type OSINTSlice } from './slices/osintSlice';
import { createPlatformTOSSlice, type PlatformTOSSlice } from './slices/platformTOSSlice';
import { createPrivySlice, type PrivySlice } from './slices/privySlice';
import {
  createPublisherAgreementSlice,
  type PublisherAgreementSlice,
} from './slices/publisherAgreementSlice';
import { createReferralCodesSlice, type ReferralCodesSlice } from './slices/referralCodesSlice';
import { createRFIAttachmentsSlice, type RFIAttachmentsSlice } from './slices/rfiAttachmentsSlice';
import { createRFIMessagesSlice, type RFIMessagesSlice } from './slices/rfiMessagesSlice';
import { createRFISlice, type RFISlice } from './slices/rfiSlice';
import {
  createServiceOfferingsSlice,
  type ServiceOfferingsSlice,
} from './slices/serviceOfferingsSlice';
import { createServiceWizardSlice, type ServiceWizardSlice } from './slices/serviceWizardSlice';
import {
  createSessionProvisionSlice,
  type SessionProvisionSlice,
} from './slices/sessionProvisionSlice';
import { createSessionsSlice, type SessionsSlice } from './slices/sessionsSlice';
import { createSsdSlice, type SsdSlice } from './slices/ssdSlice';
import { createTokensSlice, type TokensSlice } from './slices/tokensSlice';
import {
  createUserTransactionsSlice,
  type UserTransactionsSlice,
} from './slices/userTransactionsSlice';
import {
  createSearchSelectionSlice,
  type SearchSelectionSlice,
} from './slices/searchSelectionSlice';

// Combined store type
export type StoreState = BuilderSlice &
  AuthSlice &
  OrganizationSlice &
  DatasetsSlice &
  DatasetQueriesSlice &
  ApplicationsSlice &
  MarkupSlice &
  TokensSlice &
  OrganizationTokensSlice &
  OrganizationTransactionsSlice &
  UserTransactionsSlice &
  AnalyticsSlice &
  PlatformTOSSlice &
  PrivySlice &
  PublisherAgreementSlice &
  ConfigSlice &
  FeatureFlagsSlice &
  OrgInvoiceRequestsSlice &
  GiftCardsSlice &
  ReferralCodesSlice &
  SessionsSlice &
  SessionProvisionSlice &
  SsdSlice &
  DatasetWizardSlice &
  ItemsSlice &
  OSINTSlice &
  MissionPlanningSlice &
  MissionConfigSlice &
  MissionBriefingSlice &
  MissionCreateSlice &
  MissionCreateSearchContextSlice &
  MissionsSlice &
  MissionRunsSlice &
  MissionRunResultsSlice &
  ServiceOfferingsSlice &
  RFISlice &
  RFIAttachmentsSlice &
  RFIMessagesSlice &
  ServiceWizardSlice &
  ConsultantProfileSlice &
  SearchSelectionSlice;

export const STORE_VERSION = 2;

/**
 * Migration function for Zustand persist.
 * GW-2413: Targeted 1->2 migration to clear stale publisherAgreementStatus while preserving auth state.
 * For versions < 1, clears persisted state entirely (forces re-login).
 */
export function migrateStore(persistedState: unknown, version: number): Record<string, unknown> {
  // Pre-auth versions (< 1): wipe everything
  if (version < 1) {
    return {};
  }

  // Version 1->2 migration (GW-2413): Clear stale publisher agreement state
  if (version === 1) {
    const state = persistedState as Record<string, unknown>;
    return {
      // Preserve auth state
      user: state.user,
      isAuthenticated: state.isAuthenticated,
      currentOrg: state.currentOrg,
      userOrganizations: state.userOrganizations,
      // Clear stale publisher agreement state (publisherAgreementStatus, hasCheckedStatus)
      // These will be re-fetched with correct currentVersion on next load
    };
  }

  // Future versions: return as-is
  return persistedState as Record<string, unknown>;
}

// Create the store with combined slices
export const useStore = create<StoreState>()(
  devtools(
    persist(
      (...a) => ({
        ...createBuilderSlice(...a),
        ...createAuthSlice(...a),
        ...createOrganizationSlice(...a),
        ...createDatasetsSlice(...a),
        ...createDatasetQueriesSlice(...a),
        ...createApplicationsSlice(...a),
        ...createMarkupSlice(...a),
        ...createTokensSlice(...a),
        ...createOrganizationTokensSlice(...a),
        ...createOrganizationTransactionsSlice(...a),
        ...createUserTransactionsSlice(...a),
        ...createAnalyticsSlice(...a),
        ...createPlatformTOSSlice(...a),
        ...createPrivySlice(...a),
        ...createPublisherAgreementSlice(...a),
        ...createConfigSlice(...a),
        ...createFeatureFlagsSlice(...a),
        ...createOrgInvoiceRequestsSlice(...a),
        ...createGiftCardsSlice(...a),
        ...createReferralCodesSlice(...a),
        ...createSessionsSlice(...a),
        ...createSessionProvisionSlice(...a),
        ...createSsdSlice(...a),
        ...createDatasetWizardSlice(...a),
        ...createItemsSlice(...a),
        ...createOSINTSlice(...a),
        ...createMissionPlanningSlice(...a),
        ...createMissionConfigSlice(...a),
        ...createMissionBriefingSlice(...a),
        ...createMissionCreateSlice(...a),
        ...createMissionCreateSearchContextSlice(...a),
        ...createMissionsSlice(...a),
        ...createMissionRunsSlice(...a),
        ...createMissionRunResultsSlice(...a),
        ...createServiceOfferingsSlice(...a),
        ...createRFISlice(...a),
        ...createRFIAttachmentsSlice(...a),
        ...createRFIMessagesSlice(...a),
        ...createServiceWizardSlice(...a),
        ...createConsultantProfileSlice(...a),
        ...createSearchSelectionSlice(...a),
      }),
      {
        name: 'gw-storage',
        version: STORE_VERSION,
        migrate: migrateStore,
        partialize: (state) => ({
          // Persist auth state, current org, and user organizations
          user: state.user,
          isAuthenticated: state.isAuthenticated,
          currentOrg: state.currentOrg,
          userOrganizations: state.userOrganizations,
          // Persist publisher agreement status for E2E tests and session continuity
          publisherAgreementStatus: state.publisherAgreementStatus,
          hasCheckedStatus: state.hasCheckedStatus,
          // GW-10447: only the mission list itself is persisted, not its
          // fetch-status map. The list must survive a reload so a deep link
          // to /personal/missions/:missionId (GW-5445/GW-7870) can resolve
          // the mission before MissionRunsPage's own runs fetch completes.
          // The fetch-status map must NOT survive a reload -- rehydrating an
          // already-'ready' status defeats MissionListPage's idle-only mount
          // effect (its fetch action never re-fires), so the list silently
          // goes stale forever. Letting the slice's own initial 'idle' state
          // fill the gap on every fresh mount is what makes the refetch happen.
          missionsByScope: state.missionsByScope,
          // GW-6344: Persist wizard draft so multi-step service wizard survives navigation
          serviceWizardDraft: state.serviceWizardDraft,
          serviceWizardStep: state.serviceWizardStep,
          // GW-7870: Persist the mission briefing draft so /marketplace/missions/create
          // survives a page refresh mid-review.
          missionBriefing: state.missionBriefing,
        }),
      }
    ),
    { name: 'general-wisdom-store' }
  )
);

// GW-6534: Register the live store with the cycle-safe orgIdAccessor so
// non-React modules (BaseApiClient, applicationsSlice, etc.) can read
// `currentOrg.id` without re-importing this module (which would close the
// slices → API clients → accessor → store cycle).
registerOrgIdStore(useStore);

// Expose store on window for E2E tests to directly set state.
// Runtime hostname check: available on dev and localhost only, never on prod.
if (
  typeof window !== 'undefined' &&
  (window.location.hostname === 'dev.generalwisdom.com' ||
    window.location.hostname.endsWith('.dev.generalwisdom.com') ||
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1')
) {
  (window as unknown as { __ZUSTAND_STORE__: typeof useStore }).__ZUSTAND_STORE__ = useStore;
}

// Convenience selectors
export const useBuilderStore = () =>
  useStore(
    useShallow((state) => ({
      messages: state.messages,
      currentInput: state.currentInput,
      phase: state.phase,
      selectedSources: state.selectedSources,
      viewPreferences: state.viewPreferences,
      addMessage: state.addMessage,
      setCurrentInput: state.setCurrentInput,
      setPhase: state.setPhase,
      toggleSourceSelection: state.toggleSourceSelection,
      setSourceSelection: state.setSourceSelection,
      toggleViewPreference: state.toggleViewPreference,
      resetBuilder: state.resetBuilder,
      getSelectedSourceCount: state.getSelectedSourceCount,
      getTotalEstimatedCost: state.getTotalEstimatedCost,
    }))
  );

export const useAuthStore = () =>
  useStore(
    useShallow((state) => ({
      user: state.user,
      isAuthenticated: state.isAuthenticated,
      isLoading: state.isLoading,
      hasLoaded: state.hasLoaded, // GW-443: Expose hasLoaded for route guards
      errors: state.errors,
      needsOrgSelection: state.needsOrgSelection,
      signUpRecovery: state.signUpRecovery, // GW-749: Recovery info for signup errors
      signIn: state.signIn,
      signOut: state.signOut,
      signUp: state.signUp,
      confirmSignUp: state.confirmSignUp,
      resendSignUpCode: state.resendSignUpCode,
      checkAuth: state.checkAuth,
      clearError: state.clearError,
      clearSignUpError: state.clearSignUpError, // GW-749: Clear signUp error and recovery
      setNeedsOrgSelection: state.setNeedsOrgSelection,
      // GW-811: Password reset actions (prevents infinite loop from object selector)
      forgotPassword: state.forgotPassword,
      confirmForgotPassword: state.confirmForgotPassword,
      passwordResetEmail: state.passwordResetEmail,
      // GW-1709: Update user profile in store
      updateUser: state.updateUser,
      // GW-2338: Force JWT refresh after profile save
      refreshUserFromToken: state.refreshUserFromToken,
    }))
  );

export const useOrganizationStore = () =>
  useStore(
    useShallow((state) => ({
      currentOrg: state.currentOrg,
      userOrganizations: state.userOrganizations,
      isLoading: state.isLoading,
      errors: state.errors,
      fetchUserOrganizations: state.fetchUserOrganizations,
      selectOrganization: state.selectOrganization,
      updateOrganization: state.updateOrganization,
      setCurrentOrganization: state.setCurrentOrganization,
      clearError: state.clearError,
      // GW-411: Pending user invitation state and actions
      pendingUserInvitations: state.pendingUserInvitations,
      fetchPendingUserInvitations: state.fetchPendingUserInvitations,
      acceptUserInvitation: state.acceptUserInvitation,
      clearPendingUserInvitations: state.clearPendingUserInvitations,
      // GW-509: Refresh organization to get updated fields
      refreshCurrentOrganization: state.refreshCurrentOrganization,
    }))
  );

export const useDatasetsStore = () =>
  useStore(
    useShallow((state) => ({
      datasets: state.datasets,
      // GW-8565: owned-mode listing state (server-side org-scoped, cursor-paginated)
      ownedDatasets: state.ownedDatasets,
      ownedCursor: state.ownedCursor,
      ownedHasMore: state.ownedHasMore,
      currentDataset: state.currentDataset,
      isLoading: state.isLoading,
      errors: state.errors,
      fetchDatasets: state.fetchDatasets,
      loadMoreDatasets: state.loadMoreDatasets, // GW-8565: owned-mode cursor pagination
      fetchDatasetById: state.fetchDatasetById, // GW-1235: Fetch single dataset from API
      fetchDatasetBySlug: state.fetchDatasetBySlug, // GW-13274: Fetch single dataset by slug from API
      selectDataset: state.selectDataset,
      createDataset: state.createDataset,
      updateDataset: state.updateDataset,
      deleteDataset: state.deleteDataset,
      clearCurrentDataset: state.clearCurrentDataset, // GW-1235: Clear current dataset
      clearError: state.clearError,
      // GW-1779: Org-level API key
      orgApiKey: state.orgApiKey,
      fetchOrgApiKey: state.fetchOrgApiKey,
      generateOrgApiKey: state.generateOrgApiKey,
      revokeOrgApiKey: state.revokeOrgApiKey,
      regenerateOrgApiKey: state.regenerateOrgApiKey,
      // GW-2008: Reveal secret actions
      revealOrgApiKey: state.revealOrgApiKey,
    }))
  );

// GW-1340: Dataset Queries store selector
// GW-1459: Added allQueries, fetchAllQueries, clearAllQueries, refreshAllQueries
export const useDatasetQueriesStore = () =>
  useStore(
    useShallow((state) => ({
      datasetQueries: state.datasetQueries,
      allQueries: state.allQueries, // GW-1459
      currentQuery: state.currentQuery,
      queriesTotal: state.queriesTotal,
      allQueriesTotal: state.allQueriesTotal, // GW-1459
      isLoading: state.queriesIsLoading,
      hasLoaded: state.queriesHasLoaded,
      lastLoaded: state.queriesLastLoaded,
      errors: state.queriesErrors,
      fetchQueries: state.fetchQueries,
      fetchAllQueries: state.fetchAllQueries, // GW-1459
      fetchQueryById: state.fetchQueryById,
      submitQuery: state.submitQuery,
      clearQueries: state.clearQueries,
      clearAllQueries: state.clearAllQueries, // GW-1459
      clearCurrentQuery: state.clearCurrentQuery,
      clearQueriesError: state.clearQueriesError,
      refreshAllQueries: state.refreshAllQueries, // GW-1459
    }))
  );

export const useApplicationsStore = () =>
  useStore(
    useShallow((state) => ({
      applications: state.applications,
      applicationsPagination: state.applicationsPagination,
      applicationsSearchQuery: state.applicationsSearchQuery,
      orgApplicationsPagination: state.orgApplicationsPagination,
      orgApplicationsSearchQuery: state.orgApplicationsSearchQuery,
      currentApplication: state.currentApplication,
      orgAppLimits: state.orgAppLimits,
      isLoading: state.isLoading,
      hasLoaded: state.hasLoaded,
      lastLoaded: state.lastLoaded,
      errors: state.errors,
      errorStatuses: state.errorStatuses,
      // GW-417: Archive functionality
      showArchivedApplications: state.showArchivedApplications,
      setShowArchivedApplications: state.setShowArchivedApplications,
      archiveApplication: state.archiveApplication,
      unarchiveApplication: state.unarchiveApplication,
      // Application CRUD
      fetchApplications: state.fetchApplications,
      forceRefreshApplications: state.forceRefreshApplications,
      loadMoreApplications: state.loadMoreApplications,
      searchApplications: state.searchApplications,
      resetApplicationsPagination: state.resetApplicationsPagination,
      fetchOrgApplications: state.fetchOrgApplications,
      loadMoreOrgApplications: state.loadMoreOrgApplications,
      searchOrgApplications: state.searchOrgApplications,
      resetOrgApplicationsPagination: state.resetOrgApplicationsPagination,
      fetchApplicationById: state.fetchApplicationById,
      createApplication: state.createApplication,
      updateApplication: state.updateApplication,
      deleteApplication: state.deleteApplication,
      uploadIcon: state.uploadIcon,
      uploadScreenshot: state.uploadScreenshot,
      setApplicationEnabled: state.setApplicationEnabled,
      clearError: state.clearError,
      clearCurrentApplication: state.clearCurrentApplication,
    }))
  );

// GW-8285: Sessions store selector — the authoritative session lifecycle
// slice (create/launch, list/search/paginate, revoke/expire/renew, and the
// Session Companion Bar's activeLaunchedSession/appTabRef fields). Split out
// of applicationsSlice.ts; use this for all session-related state/actions.
export const useSessionsStore = () =>
  useStore(
    useShallow((state) => ({
      sessions: state.sessions,
      sessionsPagination: state.sessionsPagination,
      sessionsSearchQuery: state.sessionsSearchQuery,
      isLoading: state.isLoading,
      hasLoaded: state.hasLoaded,
      lastLoaded: state.lastLoaded,
      errors: state.errors,
      errorStatuses: state.errorStatuses,
      createSession: state.createSession,
      createSessionAndLaunch: state.createSessionAndLaunch,
      fetchUserSessions: state.fetchUserSessions,
      fetchAllUserSessions: state.fetchAllUserSessions,
      loadMoreSessions: state.loadMoreSessions,
      searchSessions: state.searchSessions,
      resetSessionsPagination: state.resetSessionsPagination,
      revokeSession: state.revokeSession,
      expireSession: state.expireSession,
      renewSession: state.renewSession,
      clearError: state.clearError,
      // GW-4622: Session Companion Bar fields
      activeLaunchedSession: state.activeLaunchedSession,
      appTabRef: state.appTabRef,
      clearActiveLaunchedSession: state.clearActiveLaunchedSession,
    }))
  );

export const useMarkupStore = () =>
  useStore(
    useShallow((state) => ({
      markups: state.markups,
      isLoading: state.isLoading,
      hasLoaded: state.hasLoaded,
      lastLoaded: state.lastLoaded,
      errors: state.errors,
      fetchApplicationMarkup: state.fetchApplicationMarkup,
      clearError: state.clearError,
    }))
  );

export const useOrganizationTokensStore = () =>
  useStore(
    useShallow((state) => ({
      organizationBalance: state.organizationBalance,
      organizationTransactions: state.organizationTransactions,
      organizationSubscription: state.organizationSubscription,
      isLoading: state.isLoading,
      hasLoaded: state.hasLoaded,
      lastLoaded: state.lastLoaded,
      errors: state.errors,
      fetchOrganizationBalance: state.fetchOrganizationBalance,
      fetchOrganizationTransactions: state.fetchOrganizationTransactions,
      purchaseOrganizationTokens: state.purchaseOrganizationTokens,
      purchaseCustomTokens: state.purchaseCustomTokens,
      redeemGiftCardForOrg: state.redeemGiftCardForOrg, // Gift card redemption for organization
      showTokenPurchaseModal: state.showTokenPurchaseModal,
      openTokenPurchaseModal: state.openTokenPurchaseModal,
      closeTokenPurchaseModal: state.closeTokenPurchaseModal,
      clearError: state.clearError,
      checkIsLoading: state.checkIsLoading,
      checkHasLoaded: state.checkHasLoaded,
    }))
  );

export const useAnalyticsStore = () =>
  useStore(
    useShallow((state) => ({
      // Core analytics state
      usageAnalytics: state.usageAnalytics,
      userUsageAnalytics: state.userUsageAnalytics,
      filters: state.filters,
      isLoading: state.isLoading,
      hasLoaded: state.hasLoaded,
      errors: state.errors,
      // Core analytics actions
      fetchUsageAnalytics: state.fetchUsageAnalytics,
      fetchUserUsageAnalytics: state.fetchUserUsageAnalytics,
      clearUserUsageAnalytics: state.clearUserUsageAnalytics,
      updateFilters: state.updateFilters,
      resetFilters: state.resetFilters,
      clearError: state.clearError,
      // GW-301: Application-specific analytics
      applicationUsageAnalytics: state.applicationUsageAnalytics,
      fetchApplicationUsageAnalytics: state.fetchApplicationUsageAnalytics,
      clearApplicationUsageAnalytics: state.clearApplicationUsageAnalytics,
    }))
  );

export const usePlatformTOSStore = () =>
  useStore(
    useShallow((state) => ({
      currentTOSVersion: state.currentTOSVersion,
      acceptedTOSVersion: state.acceptedTOSVersion,
      requiresReacceptance: state.requiresReacceptance,
      tosCheckLoading: state.tosCheckLoading,
      tosContent: state.tosContent,
      changesSummary: state.changesSummary,
      effectiveDate: state.effectiveDate,
      checkPlatformTOS: state.checkPlatformTOS,
      acceptPlatformTOS: state.acceptPlatformTOS,
      loadAcceptedVersion: state.loadAcceptedVersion,
      clearTOSState: state.clearTOSState,
    }))
  );

export const usePublisherAgreementStore = () =>
  useStore(
    useShallow((state) => ({
      publisherAgreementContent: state.publisherAgreementContent,
      publisherAgreementStatus: state.publisherAgreementStatus,
      publisherAgreementLoading: state.publisherAgreementLoading,
      publisherAgreementError: state.publisherAgreementError,
      publisherAgreementNeedsReacceptance: state.publisherAgreementNeedsReacceptance,
      fetchPublisherAgreement: state.fetchPublisherAgreement,
      checkPublisherAgreementStatus: state.checkPublisherAgreementStatus,
      acceptPublisherAgreement: state.acceptPublisherAgreement,
      clearPublisherAgreementState: state.clearPublisherAgreementState,
      clearPublisherAgreementError: state.clearPublisherAgreementError,
    }))
  );

export const useConfigStore = () =>
  useStore(
    useShallow((state) => ({
      pricingConfig: state.pricingConfig,
      isLoading: state.isLoading,
      hasLoaded: state.hasLoaded,
      lastLoaded: state.lastLoaded,
      errors: state.errors,
      fetchPricingConfig: state.fetchPricingConfig,
      clearConfigError: state.clearConfigError,
    }))
  );

// GW-919: Feature Flags store selector
export const useFeatureFlagsStore = () =>
  useStore(
    useShallow((state) => ({
      featureFlags: state.featureFlags,
      isLoading: state.featureFlagsIsLoading,
      hasLoaded: state.featureFlagsHasLoaded,
      lastLoaded: state.featureFlagsLastLoaded,
      errors: state.featureFlagsErrors,
      batchLoading: state.featureFlagsBatchLoading,
      fetchFeatureFlag: state.fetchFeatureFlag,
      fetchAllKnownFlags: state.fetchAllKnownFlags,
      clearFeatureFlags: state.clearFeatureFlags,
      clearFeatureFlagError: state.clearFeatureFlagError,
    }))
  );

// GW-1257: Org Invoice Requests store selector
export const useOrgInvoiceRequestsStore = () =>
  useStore(
    useShallow((state) => ({
      invoiceRequests: state.invoiceRequests,
      isLoading: state.isLoading,
      hasLoaded: state.hasLoaded,
      lastLoaded: state.lastLoaded,
      errors: state.errors,
      fetchOrgInvoiceRequests: state.fetchOrgInvoiceRequests,
      submitInvoiceRequest: state.submitInvoiceRequest,
      cancelInvoiceRequest: state.cancelInvoiceRequest,
      clearInvoiceRequestsError: state.clearInvoiceRequestsError,
      checkIsLoading: state.checkIsLoading,
      checkHasLoaded: state.checkHasLoaded,
    }))
  );

// Gift Cards store selector
export const useGiftCardsStore = () =>
  useStore(
    useShallow((state) => ({
      giftCards: state.giftCards,
      currentGiftCard: state.currentGiftCard,
      giftCardsPagination: state.giftCardsPagination,
      isLoading: state.isLoading,
      hasLoaded: state.hasLoaded,
      lastLoaded: state.lastLoaded,
      errors: state.errors,
      fetchGiftCards: state.fetchGiftCards,
      fetchGiftCard: state.fetchGiftCard,
      loadMoreGiftCards: state.loadMoreGiftCards,
      createGiftCard: state.createGiftCard,
      cancelGiftCard: state.cancelGiftCard,
      redeemGiftCard: state.redeemGiftCard,
      clearGiftCardsError: state.clearGiftCardsError,
      clearCurrentGiftCard: state.clearCurrentGiftCard,
      resetGiftCards: state.resetGiftCards,
      checkIsLoading: state.checkIsLoading,
      checkHasLoaded: state.checkHasLoaded,
    }))
  );

// GW-1842: Referral Codes store selector
export const useReferralCodesStore = () =>
  useStore(
    useShallow((state) => ({
      referralCodes: state.referralCodes,
      currentReferralCode: state.currentReferralCode,
      referralCodesPagination: state.referralCodesPagination,
      referralCodesStatusFilter: state.referralCodesStatusFilter,
      isLoading: state.referralCodesIsLoading,
      hasLoaded: state.referralCodesHasLoaded,
      lastLoaded: state.referralCodesLastLoaded,
      errors: state.referralCodesErrors,
      errorStatuses: state.referralCodesErrorStatuses,
      fetchReferralCodes: state.fetchReferralCodes,
      fetchReferralCode: state.fetchReferralCode,
      loadMoreReferralCodes: state.loadMoreReferralCodes,
      createReferralCode: state.createReferralCode,
      revokeReferralCode: state.revokeReferralCode,
      setReferralCodesStatusFilter: state.setReferralCodesStatusFilter,
      clearReferralCodesError: state.clearReferralCodesError,
      clearCurrentReferralCode: state.clearCurrentReferralCode,
      resetReferralCodes: state.resetReferralCodes,
      checkIsLoading: state.checkReferralCodesIsLoading,
      checkHasLoaded: state.checkReferralCodesHasLoaded,
    }))
  );

// GW-1203: Dataset Wizard store selector (draft-spec-centric model)
export const useDatasetWizardStore = () =>
  useStore(
    useShallow((state) => ({
      // Draft spec -- the domain state
      draftSpec: state.wizardDraftSpec,

      // Source tracking
      specSource: state.wizardSpecSource,

      // Credential secrets (parallel channel)
      credentialSecrets: state.wizardCredentialSecrets,
      credentialsChanged: state.wizardCredentialsChanged,

      // Icon upload (parallel channel)
      wizardIconFile: state.wizardIconFile,
      wizardIconPreview: state.wizardIconPreview,

      // Submission
      isSubmitting: state.wizardIsSubmitting,
      errors: state.wizardErrors,

      // Actions
      initDraft: state.initDraft,
      updateDraftFields: state.updateDraftFields,
      updateDraftExtension: state.updateDraftExtension,
      updateDraftField: state.updateDraftField,
      getDraftSpec: state.getDraftSpec,
      setWizardCredentialSecrets: state.setWizardCredentialSecrets,
      setWizardIconFile: state.setWizardIconFile,
      setWizardIconPreview: state.setWizardIconPreview,
      setWizardIsSubmitting: state.setWizardIsSubmitting,
      setWizardSpecSource: state.setWizardSpecSource,
      resetWizard: state.resetWizard,
      clearWizardError: state.clearWizardError,
    }))
  );

// GW-2510: Items store selector
export const useItemsStore = () =>
  useStore(
    useShallow((state) => ({
      items: state.items,
      currentItem: state.currentItem,
      itemsLoading: state.itemsLoading,
      itemsError: state.itemsError,
      fetchItems: state.fetchItems,
      fetchItemById: state.fetchItemById,
      createItem: state.createItem,
      updateItem: state.updateItem,
      clearCurrentItem: state.clearCurrentItem,
      clearItemsError: state.clearItemsError,
    }))
  );

// OSINT Tools store selector
export const useOSINTStore = () =>
  useStore(
    useShallow((state) => ({
      applications: state.osintApplications,
      sessions: state.osintSessions,
      isLoading: state.osintIsLoading,
      hasLoaded: state.osintHasLoaded,
      lastLoaded: state.osintLastLoaded,
      errors: state.osintErrors,
      fetchApplications: state.fetchOSINTApplications,
      fetchSessions: state.fetchOSINTSessions,
      launchSession: state.launchOSINTSession,
      terminateSession: state.terminateOSINTSession,
      removeSession: state.removeOSINTSession,
      clearError: state.clearOSINTError,
      reset: state.resetOSINT,
    }))
  );

// GW-4426: Privy store selector
// privyLevel/privyState fall back to user JWT claims when setPrivyFromAuth hasn't been called
export const usePrivyStore = () =>
  useStore(
    useShallow((state) => ({
      privyLevel: state.privyLevel ?? state.user?.privyLevel ?? null,
      privyState: state.privyState ?? state.user?.privyState ?? null,
      idvStatus: state.idvStatus,
      privy2Status: state.privy2Status,
      privy2InvitationUrl: state.privy2InvitationUrl,
      isLoading: state.isLoading,
      hasLoaded: state.hasLoaded,
      lastLoaded: state.lastLoaded,
      errors: state.errors,
      isEligible: state.isEligible,
      fetchIdvStatus: state.fetchIdvStatus,
      fetchPrivy2Status: state.fetchPrivy2Status,
      initiatePrivy2Check: state.initiatePrivy2Check,
      setPrivyFromAuth: state.setPrivyFromAuth,
      clearError: state.clearError,
    }))
  );

// Convenience selectors for privy access
export const usePrivyLevel = () =>
  useStore((state) => state.privyLevel ?? state.user?.privyLevel ?? null);
export const usePrivyState = () =>
  useStore((state) => state.privyState ?? state.user?.privyState ?? null);

// GW-2604: SSD store selector
export const useSsdStore = () =>
  useStore(
    useShallow((state) => ({
      artifacts: state.artifacts,
      artifactsIsLoading: state.artifactsIsLoading,
      artifactsErrors: state.artifactsErrors,
      artifactFilters: state.artifactFilters,
      deletingIds: state.deletingIds,
      fetchArtifacts: state.fetchArtifacts,
      getDownloadUrl: state.getDownloadUrl,
      deleteArtifact: state.deleteArtifact,
      setTypeFilter: state.setTypeFilter,
      setSearchFilter: state.setSearchFilter,
      setLimitFilter: state.setLimitFilter,
      clearArtifactsError: state.clearArtifactsError,
      resetArtifacts: state.resetArtifacts,
    }))
  );

// GW-4907: Mission Planning store selector
export const useMissionPlanningStore = () =>
  useStore(
    useShallow((state) => ({
      pendingPrompt: state.pendingPrompt,
      pendingChipId: state.pendingChipId,
      missionType: state.missionType,
      planningStatus: state.planningStatus,
      streamLines: state.streamLines,
      inferredDimensions: state.inferredDimensions,
      inferenceProgress: state.inferenceProgress,
      activeCard: state.activeCard,
      confirmedCards: state.confirmedCards,
      availableDatasets: state.availableDatasets,
      selectedDatasetIds: state.selectedDatasetIds,
      identifiedEntities: state.identifiedEntities,
      primaryEntity: state.primaryEntity,
      missionName: state.missionName,
      missionClassification: state.missionClassification,
      missionPriority: state.missionPriority,
      missionDescription: state.missionDescription,
      missionTags: state.missionTags,
      phaseBreakdown: state.phaseBreakdown,
      initFromChip: state.initFromChip,
      appendStreamLine: state.appendStreamLine,
      addInferredDimension: state.addInferredDimension,
      setInferenceProgress: state.setInferenceProgress,
      completeInference: state.completeInference,
      setAvailableDatasets: state.setAvailableDatasets,
      setSelectedDatasetIds: state.setSelectedDatasetIds,
      toggleDataset: state.toggleDataset,
      confirmCard: state.confirmCard,
      unconfirmCard: state.unconfirmCard,
      setActiveCard: state.setActiveCard,
      launchMission: state.launchMission,
      resetPlanning: state.resetPlanning,
      setIdentifiedEntities: state.setIdentifiedEntities,
      updateIdentifiedEntities: state.updateIdentifiedEntities,
      setMissionConfig: state.setMissionConfig,
      setPhaseBreakdown: state.setPhaseBreakdown,
      saveAsDefaults: state.saveAsDefaults,
      setSaveAsDefaults: state.setSaveAsDefaults,
    }))
  );

// GW-5445: Mission Config store selector
export const useMissionConfigStore = () =>
  useStore(
    useShallow((state) => ({
      missionConfig: state.missionConfig,
      missionConfigStatus: state.missionConfigStatus,
      missionConfigLastFetched: state.missionConfigLastFetched,
      missionConfigError: state.missionConfigError,
      fetchMissionConfig: state.fetchMissionConfig,
      clearMissionConfig: state.clearMissionConfig,
    }))
  );

// GW-7870: Mission Briefing store selector — the draft feeding CreateMissionPage
export const useMissionBriefingStore = () =>
  useStore(
    useShallow((state) => ({
      missionBriefing: state.missionBriefing,
      setMissionBriefing: state.setMissionBriefing,
      clearMissionBriefing: state.clearMissionBriefing,
    }))
  );

// GW-8603: Mission Create launch-lifecycle store selector — launching/error/
// launched state for MissionCreatePage's handleLaunch flow. Deliberately not
// persisted (see partialize below); this is ephemeral UI state, unlike the
// missionBriefing draft it launches.
export const useMissionCreateStore = () =>
  useStore(
    useShallow((state) => ({
      launching: state.launching,
      error: state.error,
      launched: state.launched,
      startLaunch: state.startLaunch,
      launchSucceeded: state.launchSucceeded,
      launchFailed: state.launchFailed,
      resetLaunch: state.resetLaunch,
    }))
  );

// GW-8289: Mission Create search-context store selector — the gw-search-api
// recommendation/entity-extraction fetch backing the rebuilt /create page.
export const useMissionCreateSearchContextStore = () =>
  useStore(
    useShallow((state) => ({
      status: state.missionCreateSearchStatus,
      recommendedDatasets: state.missionCreateRecommendedDatasets,
      detectedEntities: state.missionCreateDetectedEntities,
      entitiesAvailable: state.missionCreateEntitiesAvailable,
      // GW-10593: per-entity hydrated DirectionTemplates captured alongside
      // the rest of the on-load search response, for MissionCreatePage's
      // entity-merge effect to thread into identifiedEntities.
      directions: state.missionCreateDirections,
      error: state.missionCreateSearchError,
      fetchSearchContext: state.fetchMissionCreateSearchContext,
      clearSearchContext: state.clearMissionCreateSearchContext,
    }))
  );

// GW-5445 (P1-11): Missions (scope-keyed) store selector + scope selector
// GW-10231: fetchMissionsForScope loads the FULL result set for a scope
// (looping GET /v1/missions?offset=N until exhausted); MissionListPage
// paginates the resulting array client-side, 20 at a time, via
// DataTablePagination — matching that component's own story pattern rather
// than refetching per page or using Load More/cursor paging.
// GW-10160: runsByMissionId/fetchRunsForMission are the pre-existing
// missionRunsSlice (GW-8054) fields, reused here so MissionListTable's
// "Runs" action menu can populate — not a separate duplicate map.
export const useMissionsStore = () =>
  useStore(
    useShallow((state) => ({
      missionsByScope: state.missionsByScope,
      missionsStatusByScope: state.missionsStatusByScope,
      missionsFilters: state.missionsFilters,
      runsByMissionId: state.runsByMissionId,
      fetchMissionsForScope: state.fetchMissionsForScope,
      fetchRunsForMission: state.fetchRunsForMission,
      setMissionsFilters: state.setMissionsFilters,
    }))
  );

export const selectMissionsByScope = (scope: MissionScope) => (state: StoreState) =>
  state.missionsByScope[scope];

// GW-8054: Per-mission runs (record-id-keyed) store selector
export const useMissionRunsStore = () =>
  useStore(
    useShallow((state) => ({
      runsByMissionId: state.runsByMissionId,
      runsStatusByMissionId: state.runsStatusByMissionId,
      fetchRunsForMission: state.fetchRunsForMission,
    }))
  );

// GW-9088: a fresh `[]` literal as the "no runs cached yet" fallback breaks
// reference-equality on every call — `useStore(selectRunsForMission(id))`
// then never sees two consecutive equal snapshots (even though the fallback
// is always logically empty), so React's `useSyncExternalStore` treats every
// render as a store change and re-renders without end ("Maximum update depth
// exceeded" in MissionRunsPage). A single stable empty-array reference, reused
// across all calls, fixes the reference-equality check without changing the
// selector's return semantics.
const EMPTY_RUNS: Run[] = [];

export const selectRunsForMission = (missionRecordId: string) => (state: StoreState) =>
  state.runsByMissionId[missionRecordId] ?? EMPTY_RUNS;

// GW-8027 (Phase 3/4): Run-results (mission+run-keyed) store selector — the
// run-results page family's data source for the poll-contract Run record
// plus its per-product payloads (Entity Graph, Evidence Register, Audit
// Trail, Cost Ledger, Event Timeline, Due Diligence Report).
export const useMissionRunResultsStore = () =>
  useStore(
    useShallow((state) => ({
      runByRunKey: state.runByRunKey,
      runStatusByRunKey: state.runStatusByRunKey,
      runErrorByRunKey: state.runErrorByRunKey,
      productsByRunKey: state.productsByRunKey,
      productStatusByRunKey: state.productStatusByRunKey,
      runProductsListStatusByRunKey: state.runProductsListStatusByRunKey,
      fetchRunResults: state.fetchRunResults,
      fetchRunProduct: state.fetchRunProduct,
      fetchAllRunProducts: state.fetchAllRunProducts,
    }))
  );

// GW-3057: Service Offerings store selector
export const useServiceOfferingsStore = () =>
  useStore(
    useShallow((state) => ({
      marketplaceOfferings: state.marketplaceOfferings,
      marketplaceNextCursor: state.marketplaceNextCursor,
      selectedOffering: state.selectedOffering,
      orgOfferings: state.orgOfferings,
      orgOfferingsNextCursor: state.orgOfferingsNextCursor,
      isLoading: state.serviceOfferingsIsLoading,
      hasLoaded: state.serviceOfferingsHasLoaded,
      lastLoaded: state.serviceOfferingsLastLoaded,
      errors: state.serviceOfferingsErrors,
      fetchMarketplaceOfferings: state.fetchMarketplaceOfferings,
      fetchOfferingBySlug: state.fetchOfferingBySlug,
      fetchOrgOfferings: state.fetchOrgOfferings,
      setSelectedOffering: state.setSelectedOffering,
      clearServiceOfferingsError: state.clearServiceOfferingsError,
      resetServiceOfferings: state.resetServiceOfferings,
    }))
  );

// GW-3057: RFI store selector
export const useRFIStore = () =>
  useStore(
    useShallow((state) => ({
      currentRFI: state.currentRFI,
      buyerRFIs: state.buyerRFIs,
      buyerRFIsNextCursor: state.buyerRFIsNextCursor,
      orgRFIs: state.orgRFIs,
      orgRFIsNextCursor: state.orgRFIsNextCursor,
      consultantRFIs: state.consultantRFIs,
      consultantRFIsNextCursor: state.consultantRFIsNextCursor,
      isLoading: state.rfiIsLoading,
      hasLoaded: state.rfiHasLoaded,
      lastLoaded: state.rfiLastLoaded,
      errors: state.rfiErrors,
      fetchBuyerRFIs: state.fetchBuyerRFIs,
      loadMoreBuyerRFIs: state.loadMoreBuyerRFIs, // GW-10607
      fetchOrgRFIs: state.fetchOrgRFIs,
      fetchConsultantRFIs: state.fetchConsultantRFIs,
      loadMoreConsultantRFIs: state.loadMoreConsultantRFIs, // GW-10607
      fetchRFI: state.fetchRFI,
      submitRFI: state.submitRFI,
      assignConsultant: state.assignConsultant,
      cancelRFI: state.cancelRFI,
      deliverRFI: state.deliverRFI,
      acceptRFI: state.acceptRFI,
      disputeRFI: state.disputeRFI,
      resolveDispute: state.resolveDispute,
      extendLTIOV: state.extendLTIOV,
      approveExtension: state.approveExtension,
      rejectExtension: state.rejectExtension,
      setCurrentRFI: state.setCurrentRFI,
      clearRFIError: state.clearRFIError,
      resetRFI: state.resetRFI,
    }))
  );

// GW-3057: RFI Messages store selector
export const useRFIMessagesStore = () =>
  useStore(
    useShallow((state) => ({
      messagesByRFI: state.messagesByRFI,
      messagesCursorByRFI: state.messagesCursorByRFI,
      unreadCountsByRFI: state.unreadCountsByRFI,
      isLoading: state.rfiMessagesIsLoading,
      errors: state.rfiMessagesErrors,
      fetchMessages: state.fetchMessages,
      sendMessage: state.sendMessage,
      markRead: state.markRead,
      clearMessagesForRFI: state.clearMessagesForRFI,
      clearRFIMessagesError: state.clearRFIMessagesError,
    }))
  );

// GW-3057: Consultant Profile store selector
export const useConsultantProfileStore = () =>
  useStore(
    useShallow((state) => ({
      consultantProfile: state.consultantProfile,
      isLoading: state.consultantProfileIsLoading,
      hasLoaded: state.consultantProfileHasLoaded,
      lastLoaded: state.consultantProfileLastLoaded,
      errors: state.consultantProfileErrors,
      fetchConsultantProfile: state.fetchConsultantProfile,
      updateConsultantProfile: state.updateConsultantProfile,
      clearConsultantProfileError: state.clearConsultantProfileError,
      resetConsultantProfile: state.resetConsultantProfile,
    }))
  );
